// Constants for all tooltips to ensure consistency across VM components

// Action limitation tooltips
export const ACTION_LIMITATION_TOOLTIPS = {
  // Permission-based tooltips
  ADMIN_ONLY: 'Only administrators can perform this action',
  MIGRATION_PRIORITY_ADMIN_ONLY: 'Only administrators can set migration priority',

  // General
  SINGLE_VM_ONLY: 'You can only perform this action on a single VM at a time',

  // Server Group
  VM_ALREADY_IN_SERVER_GROUP:
    'This VM is already in a server group. Please select a VM that is not in a server group to add.',
  ADD_SERVER_GROUP_ONE_VM: 'You can only add a single VM to a server group at a time',

  // Lease
  TENANT_VM_LEASE_ONLY: 'You can only customize lease for a tenant VM',
  CUSTOMIZE_LEASE: 'You can only customize lease for a single VM at a time',
  VM_NOT_OWNED: 'VM is not owned by you. Please select a VM owned by you to customize lease.',
  VM_NOT_LEASED: 'VM is not leased. Please select a leased VM to customize lease.',

  // Hotplug
  HOTPLUG_ENABLED: 'Hotplugging is not enabled for this VM.',
  HOTPLUG_ONE_VM: 'You can only hotplug a single VM at a time',
  VM_NOT_HOTPLUGGED: 'VM is not hotplugged. Please select a hotplugged VM to hotplug.',
  DISABLED_HOTPLUG_VM_STATE: 'You can only hotplug a VM that are in active or stopped state.',

  // Networking related
  ADD_NETWORK_INTERFACE: 'You can only add a network interface to a single VM at a time',
  REMOVE_NETWORK_INTERFACE: 'You can only remove a network interface from a single VM at a time',

  ADD_PUBLIC_IP: 'You can only add a public IP address to a single VM at a time',
  ADD_PUBLIC_IP_L2_NETWORK: 'You can only add a public IP address to a VM with managed network',
  REMOVE_PUBLIC_IP: 'You can only remove a public IP address from a single VM at a time',
  REMOVE_PUBLIC_IP_L2_NETWORK:
    'You can only remove a public IP address from a VM with managed network',
  EDIT_SECURITY_GROUPS: 'You can only edit security groups for a single VM at a time',
  EDIT_SECURITY_GROUPS_L2_NETWORK:
    'You can only edit security groups for a VM with managed network',
  EDIT_PORT_SECURITY: 'You can only edit port security for a single VM at a time',
  EDIT_PORT_SECURITY_L2_NETWORK: 'You can only edit port security for a VM with managed network',

  // VM management actions
  RENAME_VM: 'You can only rename a single VM at a time',
  CHANGE_OWNER_VM: 'You can only change owner of a single VM at a time',
  SNAPSHOT_VM: 'You can only create a snapshot for a single VM at a time',
  CLONE_VM: 'You can only clone a single VM at a time',
  RESIZE_VM: 'You can only resize a single VM at a time',
  CONFIRM_RESIZE_VM: 'You can only confirm resize for a single VM at a time',
  NOT_RESIZED: 'You can only confirm resize for VMs that are in a resized state',
  ALREADY_RESIZED: 'This VM is already in a resized state',
  EDIT_METADATA: 'You can only edit metadata for a single VM at a time',
  CONSOLE_VM: 'You can only open console for a single VM at a time',
  UPDATE_MIGRATION_PRIORITY: 'You can only update migration priority for a single VM at a time',
  REBUILD_VM: 'You can only rebuild a single VM at a time',
  EDIT_METADATA_VM_RESIZED: 'You cannot Edit Metadata for a VM in resized state.',

  // Volume management
  MANAGE_VOLUMES: 'You can only manage volumes for VMs that are running, suspended or stopped',
  MANAGE_VOLUMES_STATE: 'You can only manage volumes for a running, suspended or stopped VM',

  // Power actions
  START: 'You can only start VMs that are in a stopped state',
  STOP: 'You can only stop VMs that are currently running',
  REBOOT: 'You can only reboot VMs that are currently running',
  SUSPEND: 'You can only suspend VMs that are currently running',
  RESUME: 'You can only resume VMs that are in a suspended state',
  START_ONE_VM: 'You can only start a single VM at a time',
  STOP_ONE_VM: 'You can only stop a single VM at a time',
  PAUSE: 'You can only pause VMs that are currently running',
  UNPAUSE: 'You can only unpause VMs that are in a paused state',
  CANNOT_START_RUNNING: 'You can only start VMs that are in a stopped state',
  CANNOT_STOP_STOPPED: 'You can only stop VMs that are currently running',

  // Rescue actions
  RESCUE: 'You can only rescue VMs that are not in a rescued state',
  ALREADY_RESCUED: 'This VM is already in a rescued state',
  UNRESCUE: 'You can only unrescue VMs that are in a rescued state',
  ALREADY_NOT_RESCUED: 'You can only unrescue a rescued VM',

  // Delete on termination
  DELETE_ON_TERMINATION_ENABLE:
    'You can only enable delete on termination for VMs that are active, paused or resized',
  DELETE_ON_TERMINATION_DISABLE:
    'You can only disable delete on termination for VMs that are active, paused or resized',

  // Snapshot limitations
  GPU_PASSTHROUGH_SNAPSHOT: 'Snapshot is not supported for GPU passthrough VMs',

  // Volume state tooltips
  ALREADY_ENABLED: 'Root volume delete on termination is already enabled',
  ALREADY_DISABLED: 'Root volume delete on termination is already disabled',

  // Enable/Disable Root Volume Delete on Termination
  ENABLE_DISABLE_ROOT_VOLUME_DELETE_ON_TERMINATION_SINGLE_VM:
    'You can only enable or disable delete on VM deletion for a single VM at a time',
  ENABLE_DISABLE_ROOT_VOLUME_DELETE_ON_TERMINATION:
    'You can only enable or disable delete on VM deletion for VMs that are active, paused or resized',
}

// Regular tooltip constants (non-disabled state)
export const TOOLTIP_MESSAGES = {
  // Basic action tooltips
  CONSOLE: 'Console',
  DELETE: 'Delete',
  EDIT: 'Edit',
  START: 'Start',
  STOP: 'Stop',

  // Feature-specific tooltips
  GPU_PASSTHROUGH_SNAPSHOT: 'Snapshot is not supported for GPU passthrough VMs',
  SERVER_GROUP_TOOLTIP: 'Server groups allow you to define anti-affinity rules for VMs',

  // Power action descriptions
  REBOOT: 'Reboot the VM',
  HARD_REBOOT: 'Force a hard reboot of the VM',
  SUSPEND: 'Suspend the VM',
  RESUME: 'Resume the VM',

  // Rescue action descriptions
  RESCUE: 'Boot VM in rescue mode',
  UNRESCUE: 'Exit rescue mode',
}

export const VOLUME_BACKED_VM_ERROR_MSG =
  'You can not attach data volumes to VMs using ephemeral root disk. Create a VM using a volume as root disk before you can attach more data volumes to it.'

export const VM_SNAPSHOT_WARNING_MSG =
  'To attach data volumes, please create the VM using a snapshot of type VM Volume Snapshot.'

export const VOLUME_BACKED_VM_WARNING_MSG =
  'This VM configuration uses ephemeral storage for the root disk. To attach data volumes, create the VM using a volume as the root disk.'

export const DISABLED_VOLUME_BACKED_VM_MSG =
  'To attach a volume, select a VM that uses a volume as the root disk. The currently selected VM uses an ephemeral root disk.'
